import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { useToast } from '@/shared/components/ui/Toast';
import { useUpdateUserLocations, useLocations } from '../hooks/useAdmin';
import type { UserWithLocations } from '../services/usersService';

const userLocationsSchema = z.object({
  location_ids: z.array(z.string()),
});

type UserLocationsFormData = z.infer<typeof userLocationsSchema>;

interface UserLocationsFormProps {
  isOpen: boolean;
  onClose: () => void;
  user?: UserWithLocations;
}

export function UserLocationsForm({
  isOpen,
  onClose,
  user,
}: UserLocationsFormProps) {
  const { showToast } = useToast();
  const { data: locations } = useLocations();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UserLocationsFormData>({
    resolver: zodResolver(userLocationsSchema),
  });

  const updateMutation = useUpdateUserLocations();

  useEffect(() => {
    if (isOpen && user) {
      reset({
        location_ids: user.locations.map((loc) => loc.id),
      });
    }
  }, [isOpen, user, reset]);

  const onSubmit = async (data: UserLocationsFormData) => {
    if (!user) return;

    try {
      await updateMutation.mutateAsync({
        user_id: user.id,
        location_ids: data.location_ids,
      });
      showToast('User locations updated successfully', 'success');
      onClose();
      reset();
    } catch (error) {
      console.error('Error updating user locations:', error);
      showToast(
        error instanceof Error
          ? error.message
          : 'Failed to update user locations',
        'error'
      );
    }
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Edit Locations - ${user?.name}`}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Assigned Locations
          </label>
          <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3">
            {locations?.map((location) => (
              <label
                key={location.id}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  value={location.id}
                  {...register('location_ids')}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-900">{location.name}</span>
              </label>
            ))}
          </div>
          {errors.location_ids && (
            <p className="mt-1 text-sm text-red-600">
              {errors.location_ids.message}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
